import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { cloneDeep } from 'lodash';

import { Store } from '@ngrx/store';

import { selectScrollPosition, selectSelectedChannel, selectUiState, selectUser } from '../../../../store/app.selectors';

import { BaseComponent } from '../../../shared/components/base/base.component';
import { uiInitialState } from '../../../shared/store/ui.reducer';
import { setShowScrollButton } from '../../../shared/store/ui.actions';
import { IUserProfile } from '../../../user/interfaces/IUserProfile';
import { IChannel } from '../../interfaces/IChannel';
import { WorkspaceFeatures } from '../../interfaces/IWorkspace';
import { MessagesService } from '../../services/messages.service';
import { PermissionsService } from '../../services/permissions.service';
import { WebsocketService } from '../../services/websocket.service';
import { AppStateWithChat } from '../../store/chat.reducer';

@Component({
  selector: 'ngx-channel-body',
  templateUrl: './channel-body.component.html',
  styleUrls: ['./channel-body.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChannelBodyComponent extends BaseComponent implements OnInit {
  @Output() linkClicked = new EventEmitter<boolean>();

  private store = inject(Store<AppStateWithChat>);
  public chatService = inject(WebsocketService);
  public messagesService = inject(MessagesService);
  private permissionsService = inject(PermissionsService);

  chatBodyElement = viewChild<ElementRef<HTMLElement>>('chatBody');

  ui = toSignal(this.store.select(selectUiState), { initialValue: uiInitialState });
  user = toSignal(this.store.select(selectUser), { initialValue: null as IUserProfile | null });

  storeSelectedChannel = toSignal(this.store.select(selectSelectedChannel), {
    initialValue: null as IChannel | null,
  });
  scrollPosition = toSignal(this.store.select(selectScrollPosition), {
    initialValue: uiInitialState.scrollPosition,
  });

  selectedChannel = signal<IChannel | null>(null);
  profileInfo = computed(() => this.user());
  showScrollButton = computed(() => this.ui().showScrollButton);
  noHasConsent = computed(
    () =>
      this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.validateOptInOptOut]) &&
      !this.selectedChannel()?.enabled
  );

  currentDate = new Date();

  constructor() {
    super();
    effect(() => {
      const channel = this.storeSelectedChannel();
      this.selectedChannel.set(cloneDeep(channel));
    });

    effect((onCleanup) => {
      const position = this.scrollPosition();
      const timeoutId = setTimeout(() => this.scrollToPosition(position));
      onCleanup(() => clearTimeout(timeoutId));
    });
  }

  ngOnInit() {
    // lifecycle hook kept for compatibility with parent init order
  }

  showDivider(date: Date, previousDate: Date) {
    const convertPreviousDate = new Date(previousDate);
    const currentDate = new Date(date);
    return currentDate.getDate() != convertPreviousDate.getDate();
  }

  scrollToPosition(position: number): void {
    const element = this.chatBodyElement()?.nativeElement;
    if (element?.scrollTop) {
      element.scrollTop = position;
    }
  }

  onScroll() {
    const element = this.chatBodyElement()?.nativeElement;
    if (!element) {
      return;
    }
    const top = element.scrollHeight - element.offsetHeight;
    const position = element.scrollTop * -1;
    const isOnTop = top - position < 50 && element.scrollTop !== 0;
    const isOnBottom = position < 200;
    if (isOnTop) {
      this.messagesService.getOldMessages();
    }
    if (!isOnBottom) {
      this.store.dispatch(setShowScrollButton({ value: true }));
    } else {
      this.store.dispatch(setShowScrollButton({ value: false }));
      const channel = this.selectedChannel();
      if (channel?.unread && !channel.unreadMessages) {
        this.messagesService.checkClearScrollMessage();
      }
    }
  }

  handleLinkClick(data: boolean) {
    this.linkClicked.emit(data);
  }
}
